import React, { useEffect, useState } from 'react';
import { array, bool, func, object, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { debounce, get, has, isEmpty } from 'lodash';

import template from 'url-template';

import FormControl from '@material-ui/core/FormControl';

import AutoComplete from '../../components/FormFields/Autocomplete';
import EsmsDialog from '../../components/EsmsDialog';
import TextArea from '../../components/FormFields/TextArea';

import useApi from '../../hooks/useApi';
import lambdaBaseApi from '../../apis/lambda';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as ContactActions from '../../actions/contactActions';
import * as ServiceManagementActions from '../../actions/serviceManagementActions';
import * as SvcRequestActions from '../../actions/svcRequestActions';
import * as UiActions from '../../actions/uiActions';

import editModalClasses from './EditEntityModal.module.css';
import createServiceRequestModalClasses from './CreateServiceRequestModal.module.css';

const classes = { ...createServiceRequestModalClasses, ...editModalClasses };

const AreasUrl = template.parse('/sites/{siteId}/areas{?searchFilter,limit}');
const ClientsUrl = template.parse('/companies/{companyId}/clients{?searchFilter,limit}');
const ContactsUrl = template.parse('/clients/{clientId}/contacts{?searchFilter,limit}');
const EquipmentUrl = template.parse('/sites/{siteId}/equipment{?searchFilter,limit}');
const IssueTypesUrl = template.parse('/issuetypes');
const SitesUrl = template.parse('/clients/{clientId}/sites{?searchFilter,limit}');
const SystemsUrl = template.parse('/sites/{siteId}/systems{?searchFilter,limit}');

const ValidationSchema = Yup.object().shape({
    summary:       Yup.string(),
    description:   Yup.string(),
    priority:      Yup.string().required(),
    issueTypes:    Yup.array().required(),
    clientContact: Yup.string(),
    client:        Yup.string().required(),
    site:          Yup.string().required(),
    target:        Yup.string().required(),
    area:          Yup.array()
});

const DEFAULT_FORM_VALUES = {
    summary:       '',
    description:   '',
    priority:      {id: 0, name: 'Low'},
    issueTypes:    [],
    clientContact: '',
    client:        '',
    site:          '',
    target:        '',
    area:          []
};

const InitialValues = {...DEFAULT_FORM_VALUES};

const formattedPriorities = [
    {
        items: [
            {
                id:   2,
                name: 'High'
            },
            {
                id:   1,
                name: 'Medium'
            },
            {
                id:   0,
                name: 'Low'
            }
        ],
        name:      'Priorities',
        itemProps: {
            type: 'priorities'
        }
    }
];

const DEFAULT_LIMIT = 100;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const CreateServiceRequestModal = ({
    close,
    clearErrors,
    companyInfo,
    createSvcRequest,
    currentUser,
    errors,
    fetchServiceItemList,
    fetchSiteContacts,
    isLoading,
    open,
    rule = null,
    siteContacts,
    title,
    toggleLoading,
    ...props
}) => {
    const [issueTypes, fetchIssueTypes] = useApi(() => ({
        url: IssueTypesUrl.expand()
    }), lambdaBaseApi);

    const [clients, fetchClients] = useApi((searchFilter = '') => ({
        url: ClientsUrl.expand({
            companyId: companyInfo.companyId,
            searchFilter,
            limit:     DEFAULT_LIMIT
        })
    }));

    const [sites, fetchSites] = useApi((clientId, searchFilter = '') => ({
        url: SitesUrl.expand({
            clientId,
            searchFilter,
            limit: DEFAULT_LIMIT
        })
    }));

    const [clientContacts, fetchClientContantsList] = useApi((clientId, searchFilter = '') => ({
        url: ContactsUrl.expand({
            clientId,
            searchFilter,
            limit: DEFAULT_LIMIT
        })
    }));

    const [areas, fetchAreas] = useApi((siteId, searchFilter = '') => ({
        url: AreasUrl.expand({
            siteId,
            searchFilter,
            limit: DEFAULT_LIMIT
        })
    }));

    const [equipment, fetchEquipment] = useApi((siteId, searchFilter = '') => ({
        url: EquipmentUrl.expand({
            siteId,
            searchFilter,
            limit: DEFAULT_LIMIT
        })
    }));

    const [systems, fetchSystems] = useApi((siteId, searchFilter = '') => ({
        url: SystemsUrl.expand({
            siteId,
            searchFilter,
            limit: DEFAULT_LIMIT
        })
    }));

    const [formattedIssueTypes, setFormattedIssueTypes] = useState([]);
    const [formattedClients, setFormattedClients] = useState([]);
    const [formattedSites, setFormattedSites] = useState([]);
    const [formattedTargets, setFormattedTargets] = useState([]);
    const [formattedClientContacts, setFormattedClientContacts] = useState([]);
    const [formattedAreas, setFormattedAreas] = useState([]);

    const [selectedEquipment, setSelectedEquipment] = useState();
    const [selectedSystem, setSelectedSystem] = useState();

    const [ loading, setLoading ] = useState(false);
    const [ isDisabled, setIsDisabled ] = useState(false);

    const [ isClientRuleLoaded, setIsClientRuleLoaded ] = useState(false);

    const formik = useFormik({
        initialValues:      InitialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {

            const formatIssueTypes = (issueTypes) => {
                return issueTypes.map(type => type.id);
            };

            let body = {
                clientId:        values.client.id,
                clientContactId: values.clientContact.id,
                companyId:       companyInfo.companyId,
                summary:         values.summary,
                description:     values.description,
                issueTypes:      formatIssueTypes(values.issueTypes),
                priority:        values.priority.name,
                requesterEmail:  currentUser.email,
                requesterName:   currentUser.userName,
                requesterPhone:  currentUser.phone,
                siteId:          values.site.id,
                areaId:          values.area ? values.area.id : null,
                equipmentId:     selectedEquipment ? selectedEquipment.id : null,
                systemId:        selectedSystem ? selectedSystem.id : null
            };

            let isUpdated;

            if (!isLoading) {
                toggleLoading(true);
                isUpdated = await createSvcRequest(body);
            }

            if (isUpdated) {
                handleClose();
            } else {
                toggleLoading(false);
            }
        }
    });

    const emptyGuidCheck = guid => {
        return guid === EMPTY_GUID;
    };

    const initIssueTypes = () => {
        if (has(rule, 'type')) return;

        const targetIssueTypes = [];
        const issueTypeNames = get(rule, 'issueTypes', []);

        if (issueTypeNames.length && issueTypes.data && issueTypes.data.length) {
            issueTypes.data.forEach(issueType => {
                if (issueTypeNames.includes(issueType.name)) {
                    targetIssueTypes.push({
                        id:   issueType.id,
                        name: issueType.name
                    });
                }
            });

            formik.setFieldValue('issueTypes', targetIssueTypes, false);
        }
    };

    const initClient = () => {
        let targetClient;

        if (has(rule, 'type') && rule.type === 'client') {
            targetClient = clients.data.find(client => client.id === rule.id);
        } else if (!emptyGuidCheck(rule.clientId)) {
            targetClient = clients.data.find(client => client.id === rule.clientId);
        }

        if (targetClient) {
            const preparedClient = {
                name: targetClient.name,
                id:   targetClient.id
            };

            formik.setFieldValue('client', preparedClient, false);

            fetchSites(targetClient.id);
        }
    };

    const initSite = () => {
        let targetSite;

        if (has(rule, 'type') && rule.type === 'site') {
            targetSite = sites.data.find(site => site.id === rule.id);
        } else if (!emptyGuidCheck(rule.siteId)) {
            targetSite = sites.data.find(site => site.id === rule.siteId);
        }

        if (targetSite) {
            const preparedSite = {
                name: targetSite.name,
                id:   targetSite.id
            };

            formik.setFieldValue('site', preparedSite, false);

            fetchEquipment(targetSite.id);
            fetchSystems(targetSite.id);
            fetchAreas(targetSite.id);
        }
    };

    const initTarget = () => {
        let target,
            preparedTarget;

        if (rule && rule.equipmentId && !emptyGuidCheck(rule.equipmentId)) {
            target = equipment.data.find(item => item.id === rule.equipmentId);
            preparedTarget = {
                name: target.name,
                id:   target.id
            };

            setSelectedEquipment(preparedTarget);
        } else if (rule && rule.systemId && !emptyGuidCheck(rule.systemId)) {
            target = systems.data.find(item => item.id === rule.systemId);
            preparedTarget = {
                name: target.name,
                id:   target.id
            };

            setSelectedSystem(preparedTarget);
        }

        if (preparedTarget) {
            formik.setFieldValue('target', preparedTarget, false);
        }
    };

    const initAreas = () => {
        const targetAreas = [];
        const areaIds = get(rule, 'areas', []);

        if (areaIds && areaIds.length && areas.data && areas.data.length) {
            areas.data.forEach(area => {
                if (areaIds.includes(area.id)) {
                    targetAreas.push({
                        id:   area.id,
                        name: area.name
                    });
                }
            });

            formik.setFieldValue('area', targetAreas, false);
        }
    };

    useEffect(() => {
        if (rule && !isEmpty(rule)) {
            if (has(rule, 'type')) {
                setIsDisabled(false);
            } else {
                setIsDisabled(true);
            }
        }
    }, [rule]);

    useEffect(() => {
        if (open) {
            fetchIssueTypes();
            fetchClients();
        } else {
            setIsClientRuleLoaded(false);
        }
    }, [open]);

    useEffect(() => {
        if (issueTypes.data && open) {
            const payload = issueTypes.data.map(issueType => {
                issueType.name = issueType.label;
                if (issueType.name === 'healthAndSafety') issueType.name = 'Health and Safety';

                return { id: issueType.id, name: issueType.name };
            });

            const formatted = [
                {
                    items:     [...payload].sort(sorter),
                    name:      'Issue Types',
                    itemProps: { type: 'issueTypes'}
                }
            ];

            setFormattedIssueTypes(formatted);

            if (rule && !isEmpty(rule)) {
                initIssueTypes();
            }
        }
    }, [issueTypes]);

    useEffect(() => {
        if (clients.data && open) {
            const payload = clients.data.map(client => ( { id: client.id, name: client.name} ));

            setLoading(false);

            const formatted = [
                {
                    items:     [...payload].sort(sorter),
                    name:      'Clients',
                    itemProps: { type: 'clients'}
                }
            ];

            setFormattedClients(formatted);

            if (rule && !isEmpty(rule) && !isClientRuleLoaded) {
                setIsClientRuleLoaded(true);
                initClient();
            }
        }
    }, [clients]);

    useEffect(() => {
        if (!process.env.contactsEnabled && clientContacts.data && open) {
            const payload = clientContacts.data.map(contact => ( { id: contact.id, name: contact.name} ));

            setLoading(false);

            const formatted = [
                {
                    items:     [...payload].sort(sorter),
                    name:      'Client Contacts',
                    itemProps: { type: 'clientContacts'}
                }
            ];

            setFormattedClientContacts(formatted);
        }
    }, [clientContacts]);

    useEffect(() => {
        if (process.env.contactsEnabled) {
            const payload = siteContacts.map(contact => ({ id: contact.id, name: `${contact.userName.FirstName} ${contact.userName.LastName}` }));
            setLoading(false);

            const formatted = [
                {
                    items:     [...payload].sort(sorter),
                    name:      'Site Contacts',
                    itemProps: { type: 'siteContacts' }
                }
            ];

            setFormattedClientContacts(formatted);
        }
    }, [siteContacts]);

    useEffect(() => {
        if (sites.data && open) {
            const payload = sites.data.map(site => ( { id: site.id, name: site.name} ));

            setLoading(false);

            const formatted = [
                {
                    items:     [...payload].sort(sorter),
                    name:      'Sites',
                    itemProps: { type: 'sites'}
                }
            ];

            setFormattedSites(formatted);

            if (rule && !isEmpty(rule)) {
                initSite();
            }
        }
    }, [sites]);

    useEffect(() => {
        if (equipment.data && systems.data && open) {
            const equipmentPayload = equipment.data.map(item => ( { id: item.id, name: item.name} ));
            const systemPayload = systems.data.map(item => ( { id: item.id, name: item.name} ));

            setLoading(false);

            const formatted = [
                {
                    items:     [...equipmentPayload].sort(sorter),
                    name:      'Equipment',
                    itemProps: { type: 'equipment'}
                },
                {
                    items:     [...systemPayload].sort(sorter),
                    name:      'Systems',
                    itemProps: { type: 'systems'}
                }
            ];

            setFormattedTargets(formatted);

            if (rule && !isEmpty(rule)) {
                initTarget();
            }
        }
    }, [equipment, systems]);

    useEffect(() => {
        if (areas.data && open) {
            const payload = areas.data.map(area => ( { id: area.id, name: area.name} ));

            setLoading(false);

            const formatted = [
                {
                    items:     [...payload].sort(sorter),
                    name:      'Areas',
                    itemProps: { type: 'areas'}
                }
            ];

            setFormattedAreas(formatted);

            if (rule && !isEmpty(rule)) {
                initAreas();
            }
        }
    }, [areas]);

    const sorter = (a, b) => {
        let nameA = a.name.toUpperCase();
        let nameB = b.name.toUpperCase();

        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }

        return 0;
    };

    const isAutoFillArrayEmpty = array => {
        if (!array || array.length < 1) {
            return true;
        }

        if (has(array[0], 'items')) {
            return isEmpty(array[0].items);
        }

        return true;
    };

    const getClients = searchFilter => {
        setLoading(true);

        if (searchFilter) {
            fetchClients(searchFilter);

            return;
        }

        fetchClients();
    };

    const getSites = searchFilter => {
        setLoading(true);

        if (searchFilter) {
            fetchSites(formik.values.client.id, searchFilter);

            return;
        }

        fetchSites(formik.values.client.id);
    };

    const getTargets = searchFilter => {
        setLoading(true);

        if (searchFilter) {
            fetchEquipment(formik.values.site.id, searchFilter);
            fetchSystems(formik.values.site.id, searchFilter);

            return;
        }

        fetchEquipment(formik.values.site.id);
        fetchSystems(formik.values.site.id);
    };

    const getContacts = async searchFilter => {
        setLoading(true);

        if (searchFilter) {

            if (process.env.contactsEnabled) {
                if (!formik.values.site.id) {
                    setLoading(false);
                    return;
                }
                await fetchSiteContacts(formik.values.client.id, formik.values.site.id);
            } else {
                fetchClientContantsList(formik.values.client.id, searchFilter);
            }

            return;
        }

        if (process.env.contactsEnabled) {
            if (!formik.values.site.id) {
                setLoading(false);
                return;
            }
            await fetchSiteContacts(formik.values.client.id, formik.values.site.id);
        } else {
            fetchClientContantsList(formik.values.client.id);
        }
    };

    const getAreas = searchFilter => {
        setLoading(true);

        if (searchFilter) {
            fetchAreas(formik.values.site.id, searchFilter);

            return;
        }

        fetchAreas(formik.values.site.id);
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        close();
    };

    const handleReset = () => {
        if (!rule) {
            formik.handleReset(InitialValues);
        } else {
            const description = get(rule, 'description', '');
            const systemEffect = get(rule, 'systemEffect', '');
            const rawPriority = get(rule, 'priority', 0);
            const priority = formattedPriorities[0].items.find(item => item.id === rawPriority);
            const recommendation = get(rule, 'recommendation', '');

            let formattedDescription = '';

            if (description && systemEffect && recommendation) {
                formattedDescription = `${description}\n\nSystem Effect: \n${systemEffect}\n\nRecommendation: \n${recommendation}`;
            } else if (description && systemEffect) {
                formattedDescription = `${description}\n\nSystem Effect: \n${systemEffect}`;
            } else if (description && recommendation) {
                formattedDescription = `${description}\n\nRecommendation: \n${recommendation}`;
            } else if (description) {
                formattedDescription = description;
            } else if (systemEffect) {
                formattedDescription = `System Effect: \n${systemEffect}`;
            } else if (recommendation) {
                formattedDescription = `Recommendation: \n${recommendation}`;
            }

            InitialValues.summary = get(rule, 'name', '');
            InitialValues.description = formattedDescription;
            InitialValues.priority = priority;

            formik.handleReset(InitialValues);
        }

        setFormattedClients([]);
        setFormattedSites([]);
        setFormattedTargets([]);
        setFormattedClientContacts([]);
        setFormattedAreas([]);
    };

    const handleClientChange = async event => {
        const {value} = event.target;

        if (value && value.id) {
            fetchSites(value.id);

            if (!process.env.contactsEnabled) {
                fetchClientContantsList(value.id);     
            }
        }

        if (value !== formik.values.client) {
            formik.setFieldValue('site', '', false);
            formik.setFieldValue('target', '', false);
            formik.setFieldValue('clientContact', '', false);
            formik.setFieldValue('area', '', false);

            formik.setTouched({
                site:          false,
                target:        false,
                clientContact: false,
                area:          false
            });

            setFormattedSites([]);
            setFormattedTargets([]);
            setFormattedClientContacts([]);
            setFormattedAreas([]);
        }

        formik.handleChange(event);
    };

    const handleSiteChange = async event => {
        const {value} = event.target;

        if (value && value.id) {
            fetchEquipment(value.id);
            fetchSystems(value.id);
            fetchAreas(value.id);
        }

        if (process.env.contactsEnabled) {
            await fetchSiteContacts(formik.values.client.id, value.id);
        }

        if (value !== formik.values.client) {
            formik.setFieldValue('target', '', false);
            formik.setFieldValue('area', '', false);

            formik.setTouched({
                target: false,
                area:   false
            });

            setFormattedTargets([]);
            setFormattedAreas([]);
        }

        formik.handleChange(event);
    };

    const handleTargetChange = event => {
        const { value } = event.target;

        switch (value.type) {
            case 'equipment':
                setSelectedEquipment(value);
                break;
            case 'systems':
                setSelectedSystem(value);
                break;
            default:
                break;
        }

        formik.handleChange(event);
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onConfirm={formik.handleSubmit}
            onRendered={handleReset}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
            className={classes.createServiceRequestModal}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/ServiceManualIconFlat.svg" alt="new equipment icon" />
                        <span>Create Service Request</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            A Service Request is a request for Service to address a particular issue.
                        </p>
                        <p>
                            You must specify a Priority, Issue Type(s), Client, Site, and Target.
                        </p>
                        <p>
                            To assign a Site Contact to this Service Request, select one from the Site Contact dropdown.
                        </p>
                        <p>
                            To associate this Service Request with an Area, select one from the Area dropdown.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextArea
                                name="summary"
                                type="text"
                                multiline={true}
                                value={formik.values.summary}
                                label="Summary"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error: classes.cssErrorUnderline,
                                        root:  classes.summaryTextArea
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.summary) && Boolean(formik.touched.summary)}
                                helperText={Boolean(formik.touched.summary) && formik.errors.summary}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextArea
                                name="description"
                                type="text"
                                multiline={true}
                                value={formik.values.description}
                                label="Description"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error: classes.cssErrorUnderline,
                                        root:  classes.summaryTextArea
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.description) && Boolean(formik.touched.description)}
                                helperText={Boolean(formik.touched.description) && formik.errors.description}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="Priority"
                                name="priority"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                onBlur={formik.handleBlur}
                                className={classes.autoComplete}
                                onChange={formik.handleChange}
                                options={formattedPriorities}
                                value={formik.values.priority}
                                error={Boolean(formik.errors.priority) && Boolean(formik.touched.priority)}
                                helperText={Boolean(formik.touched.priority) && formik.errors.priority}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="Issue Types"
                                name="issueTypes"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                onBlur={formik.handleBlur}
                                multiple={true}
                                className={classes.autoComplete}
                                value={formik.values.issueTypes}
                                onChange={formik.handleChange}
                                options={formattedIssueTypes}
                                disabled={isAutoFillArrayEmpty(formattedIssueTypes)}
                                error={Boolean(formik.errors.issueTypes) && Boolean(formik.touched.issueTypes)}
                                helperText={Boolean(formik.touched.issueTypes) && formik.errors.issueTypes}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="Client"
                                name="client"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                className={classes.autoComplete}
                                onBlur={formik.handleBlur}
                                onChange={handleClientChange}
                                onKeyDown={debounce(getClients, 1000)}
                                loading={loading}
                                options={formattedClients}
                                value={formik.values.client}
                                disabled={isAutoFillArrayEmpty(formattedClients) || isDisabled}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                                helperText={Boolean(formik.touched.client) && formik.errors.client}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="Site"
                                name="site"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                className={classes.autoComplete}
                                onBlur={formik.handleBlur}
                                onChange={handleSiteChange}
                                onKeyDown={debounce(getSites, 1000)}
                                loading={loading}
                                options={formattedSites}
                                value={formik.values.site}
                                disabled={isAutoFillArrayEmpty(formattedSites) || isDisabled}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                                helperText={Boolean(formik.touched.site) && formik.errors.site}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="Target"
                                name="target"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                className={classes.autoComplete}
                                onBlur={formik.handleBlur}
                                onChange={handleTargetChange}
                                onKeyDown={debounce(getTargets, 1000)}
                                loading={loading}
                                options={formattedTargets}
                                value={formik.values.target}
                                disabled={isAutoFillArrayEmpty(formattedSites) || isDisabled}
                                error={Boolean(formik.errors.target) && Boolean(formik.touched.target)}
                                helperText={Boolean(formik.touched.target) && formik.errors.target}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="Site Contact"
                                name="clientContact"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                className={classes.autoComplete}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                onKeyDown={debounce(getContacts, 1000)}
                                loading={loading}
                                options={formattedClientContacts}
                                value={formik.values.clientContact}
                                disabled={isAutoFillArrayEmpty(formattedClients)}
                                error={Boolean(formik.errors.clientContact) && Boolean(formik.touched.clientContact)}
                                helperText={Boolean(formik.touched.clientContact) && formik.errors.clientContact}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <AutoComplete
                                grouped
                                label="Area"
                                name="area"
                                multiple={true}
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                className={classes.autoComplete}
                                onBlur={formik.handleBlur}
                                onChange={formik.handleChange}
                                onKeyDown={debounce(getAreas, 1000)}
                                loading={loading}
                                options={formattedAreas}
                                value={formik.values.area}
                                disabled={isAutoFillArrayEmpty(formattedSites) || (rule && rule.type === 'areaItem')}
                                error={Boolean(formik.errors.area) && Boolean(formik.touched.area)}
                                helperText={Boolean(formik.touched.area) && formik.errors.area}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateServiceRequestModal.defaultProps = {
    close:       () => {},
    companyInfo: {},
    currentUser: {},
    errors:      [],
    isLoading:   false,
    open:        false,
    rule:        {},
    title:       ''
};

CreateServiceRequestModal.propTypes = {
    close:       func,
    companyInfo: object,
    currentUser: object,
    errors:      array,
    isLoading:   bool,
    open:        bool,
    rule:        object,
    title:       string
};

const mapStateToProps = state => {
    return {
        companyInfo:       state.companyInfo,
        currentUser:       state.currentUser,
        clientContactList: state.contact.clientContacts,
        errors:            state.messages.errors,
        isLoading:         state.ui.isLoading,
        siteContacts:      state.contact.siteContacts
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...ContactActions,
    ...ServiceManagementActions,
    ...SvcRequestActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateServiceRequestModal);
